import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const useNewsletter = () => {
    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

    const isValidEmail = useCallback((email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }, []);

    const handleSubscribe = useCallback(async () => {
        if (!email.trim()) {
            toast.error("Please enter your email address");
            return;
        }

        if (!isValidEmail(email)) {
            toast.error("Please enter a valid email address");
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/newsletter/subscribe`, {
                email: email.trim().toLowerCase()
            }, {
                timeout: 10000,
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            setSubmitStatus('success');
            
            // Handle different success messages from controller
            const message = response.data?.message || "Subscription successful!";
            toast.success(message);
            setEmail("");

            // Track subscription event
            if (typeof window !== 'undefined' && window.gtag) {
                window.gtag('event', 'newsletter_signup', {
                    event_category: 'engagement',
                    event_label: 'footer_newsletter'
                });
            }
        } catch (error) {
            console.error('Newsletter subscription failed:', error);
            setSubmitStatus('error');
            
            // Handle specific error responses from controller
            if (error.response?.status === 400) {
                // Already subscribed case
                const message = error.response.data?.message || "You are already subscribed!";
                toast.info(message);
                setEmail("");
            } else if (error.response?.status === 500) {
                // Server error from controller
                toast.error("Server error. Please try again later.");
            } else if (error.code === 'ECONNABORTED') {
                // Timeout error
                toast.error("Request timeout. Please check your connection and try again.");
            } else {
                // Generic error fallback
                toast.error("Subscription failed. Please try again later.");
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [email, isValidEmail]);

    const handleUnsubscribe = useCallback(async () => {
        if (!email.trim()) {
            toast.error("Please enter your email address");
            return;
        }

        if (!isValidEmail(email)) {
            toast.error("Please enter a valid email address");
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/newsletter/unsubscribe`, {
                email: email.trim().toLowerCase()
            }, {
                timeout: 10000,
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            setSubmitStatus('success');
            const message = response.data?.message || "Unsubscribed successfully!";
            toast.success(message);
            setEmail("");

        } catch (error) {
            console.error('Newsletter unsubscribe failed:', error);
            setSubmitStatus('error');
            
            if (error.response?.status === 404) {
                // Subscriber not found
                const message = error.response.data?.message || "Subscriber not found";
                toast.error(message);
            } else if (error.response?.status === 500) {
                // Server error from controller
                toast.error("Server error. Please try again later.");
            } else if (error.code === 'ECONNABORTED') {
                // Timeout error
                toast.error("Request timeout. Please check your connection and try again.");
            } else {
                // Generic error fallback
                toast.error("Unsubscribe failed. Please try again later.");
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [email, isValidEmail]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            handleSubscribe();
        }
    }, [handleSubscribe]);

    return {
        email,
        setEmail,
        isSubmitting,
        submitStatus,
        handleSubscribe,
        handleUnsubscribe,
        handleKeyPress,
        isValidEmail: isValidEmail(email)
    };
};

export default useNewsletter;